import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { useBranding } from "@/lib/branding";

// QR for a shared pulse link — generated in the browser from the link shown once at creation; the server never holds the plaintext.
const cssHsl = (name) => `hsl(${getComputedStyle(document.documentElement).getPropertyValue(name).trim()})`;

export default function PulseQr({ link, pulse, changeTitle }) {
  const brand = useBranding();
  const [qr, setQr] = useState("");
  const posterRef = useRef(null);
  useEffect(() => { QRCode.toDataURL(link, { width: 512, margin: 2, errorCorrectionLevel: "M" }).then(setQr).catch(() => setQr("")); }, [link]);
  const poster = async () => {
    const W = 1000, H = 1400, c = document.createElement("canvas");
    c.width = W; c.height = H;
    const g = c.getContext("2d");
    const ink = cssHsl("--ink"), paper = cssHsl("--card"), muted = cssHsl("--muted-foreground");
    g.fillStyle = paper; g.fillRect(0, 0, W, H);
    g.fillStyle = cssHsl("--brand-raw"); g.fillRect(0, 0, W, 18);
    g.fillStyle = ink; g.textAlign = "center";
    const MAXW = W - 80, FAM = "Manrope, Helvetica, Arial, sans-serif";
    // Shrink the font until the line fits inside the page margins.
    const fit = (text, size, weight = "", family = FAM) => { let px = size; do { g.font = `${weight} ${px}px ${family}`.trim(); px -= 2; } while (g.measureText(text).width > MAXW && px > 14); };
    fit("Five quick questions", 64, "bold"); g.fillText("Five quick questions", W / 2, 130);
    g.fillStyle = muted; const sub = "Scan to answer for yourself — anonymous, about ninety seconds."; fit(sub, 34); g.fillText(sub, W / 2, 190);
    if (pulse.show_context && changeTitle) { g.fillStyle = ink; const ctx = `${changeTitle} · ${pulse.target_name}`; fit(ctx, 36, "bold"); g.fillText(ctx, W / 2, 260); }
    const img = new Image(); img.src = await QRCode.toDataURL(link, { width: 640, margin: 1, errorCorrectionLevel: "M" });
    await new Promise((r) => { img.onload = r; });
    g.drawImage(img, (W - 640) / 2, 320, 640, 640);
    g.fillStyle = muted; const short = link.replace(/^https?:\/\//, ""); fit(short, 26, "", "monospace"); g.fillText(short, W / 2, 1030);
    ["No name, email or sign-in is asked for or stored.", "Results are shown only as group totals, once at least 5 people have answered.", `Open until ${new Date(pulse.closes_at).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}.`].forEach((t, i) => { fit(t, 28); g.fillText(t, W / 2, 1110 + i * 44); });
    g.font = `bold 24px ${FAM}`; g.fillStyle = ink;
    g.fillText(brand ? `${brand.display_name} · Powered by ChangeOS` : "Powered by ChangeOS", W / 2, H - 60);
    return c.toDataURL("image/png");
  };
  const download = async () => {
    try { const a = document.createElement("a"); a.href = await poster(); a.download = `pulse-${pulse.target_name.replace(/\W+/g, "-")}-${pulse.seq}-poster.png`; a.click(); toast.success("Poster downloaded — A4 portrait prints well"); } catch { toast.error("Couldn't build the poster"); }
  };
  const print = async () => {
    try {
      const url = await poster();
      const w = window.open("", "_blank", "noopener");
      if (!w) return toast.error("Allow pop-ups to print");
      w.document.write(`<!doctype html><title>Pulse poster</title><body style="margin:0;display:flex;justify-content:center"><img src="${url}" alt="Pulse QR poster" style="max-width:100%;max-height:100vh" onload="window.print()"></body>`);
      w.document.close();
    } catch { toast.error("Couldn't build the poster"); }
  };
  if (!qr) return null;
  return (
    <div className="mt-4 flex flex-col sm:flex-row gap-4 items-start" data-testid="pulse-qr">
      <img ref={posterRef} src={qr} alt={`QR code for the ${pulse.target_name} pulse link`} width={160} height={160} className="rounded-xl border border-border bg-card p-2 shrink-0" data-testid="pulse-qr-img" />
      <div className="text-sm">
        <div className="font-bold">QR for a noticeboard</div>
        <p className="text-subtle mt-1 leading-relaxed">The poster carries the code, what it's for, the anonymity promise and the close date — no sign-in, no app. Pin it where the people who don't read email actually stand.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" data-testid="pulse-qr-download" onClick={download} className="pp-btn-dark text-sm py-2"><Download size={14} /> Download poster (PNG)</button>
          <button type="button" data-testid="pulse-qr-print" onClick={print} className="pp-btn-ghost text-sm py-2"><Printer size={14} /> Print</button>
        </div>
      </div>
    </div>
  );
}
